import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faArchive } from "@fortawesome/pro-solid-svg-icons/faArchive";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tabs from "antd/es/tabs";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";

import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import {
  EntityType,
  EntityTypesSupportingFieldCustomization,
} from "@mapmycustomers/shared/types/entity";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, Button, Popconfirm } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { NEW_CUSTOM_FIELD } from "@app/scene/settings/util/const";
import { getCustomFieldsForEntity } from "@app/store/customFields";
import { getPhiPromotionAlertDismissed, isCurrentUserOwner } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import { getEntitySchemaFields } from "@app/store/schema";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import SettingPage from "../common/SettingPage";
import CreateEditPane from "../CustomFields/component/CreateEditPane";
import { setCreateEditCustomField, setCreateEditEntityType } from "../CustomFields/store/actions";

import BulkEditPane from "./component/BulkEditPane";
import EditPane from "./component/EditPane";
import FieldsTable from "./component/FieldsTable";
import SearchBox from "./component/SearchBox";
import TypeFilter from "./component/TypeFilter";
import VisibilityFilter from "./component/VisibilityFilter";
import styles from "./ManageFields.module.scss";
import messages from "./messages";
import { getActiveEntityType, isArchiveMode, isEditedFormVisible, isLoading } from "./store";
import {
  bulkUpdateField,
  cancelEditField,
  editField,
  enterArchiveMode,
  exitArchiveMode,
  setActiveEntityType,
  updateSchema,
} from "./store/actions";

interface Props {
  cancelEditField: typeof cancelEditField;
  customFields: CustomField[];
  editField: typeof editField.request;
  editFormVisible: boolean;
  enterArchiveMode: typeof enterArchiveMode;
  entityType: EntityTypesSupportingFieldCustomization;
  exitArchiveMode: typeof exitArchiveMode;
  isArchiveMode: boolean;
  loading: boolean;
  onBulkUpdateField: typeof bulkUpdateField.request;
  onUpdateMetadata: typeof updateMetadata.request;
  setActiveEntityType: typeof setActiveEntityType;
  setCreateEditCustomField: typeof setCreateEditCustomField;
  setCreateEditEntityType: typeof setCreateEditEntityType;
  showPhiPromotionAlert: boolean;
  updateSchema: typeof updateSchema.request;
}

const ManageFields: React.FC<Props> = ({
  cancelEditField,
  editField,
  editFormVisible,
  enterArchiveMode,
  entityType,
  exitArchiveMode,
  isArchiveMode,
  loading,
  onBulkUpdateField,
  onUpdateMetadata,
  setActiveEntityType,
  setCreateEditCustomField,
  setCreateEditEntityType,
  showPhiPromotionAlert,
  updateSchema,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const analytics = useExtendedAnalytics(
    ["Settings", "Forms & Fields", "Manage Fields"],
    useAnalytics()
  );

  const { id, tab = EntityType.COMPANY } = useParams<{
    id: string;
    tab: EntityTypesSupportingFieldCustomization;
  }>();

  const [createFormVisible, showCreateForm, hideCreateForm] = useBoolean();
  const [selectedSchemaFields, setSelectedSchemaFields] = useState<SchemaField[]>([]);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState<boolean>(false);
  const [bulkEditPaneOpen, setBulkEditPaneOpen] = useState(false);

  const selectedSchemaFieldsKeys = useMemo(
    () => selectedSchemaFields.map((schemaField) => schemaField.id),
    [selectedSchemaFields]
  );

  useEffect(() => {
    analytics.clicked([]);

    return () => {
      cancelEditField(false);
    };
  }, [analytics, cancelEditField]);

  useEffect(() => {
    setActiveEntityType(tab as EntityTypesSupportingFieldCustomization);
    updateSchema();
  }, [tab, setActiveEntityType, updateSchema]);

  useEffect(() => {
    const fieldId = parseInt(id, 10);
    if (!isNaN(fieldId) && !loading && entityType) {
      editField(fieldId);
    }
  }, [editField, entityType, id, loading]);

  const handleTabClick = useCallback(
    (key) => {
      analytics.clicked([getEntityTypeAnalyticsName(key, false, true)]);
      history.push(`${Path.SETTINGS}/${SettingPath.FORMS_MANAGE_FIELDS}/${key}`);
    },
    [analytics, history]
  );

  const handleEnterArchive = useCallback(() => {
    analytics.clicked(["View Archived Fields"]);
    enterArchiveMode();
  }, [analytics, enterArchiveMode]);

  const handleExitArchive = useCallback(() => {
    exitArchiveMode();
  }, [exitArchiveMode]);

  const handleCreateField = useCallback(() => {
    hideCreateForm();
  }, [hideCreateForm]);

  const handleAddCustomField = useCallback(() => {
    setCreateEditEntityType(entityType);
    setCreateEditCustomField(NEW_CUSTOM_FIELD);

    showCreateForm();
  }, [entityType, setCreateEditCustomField, setCreateEditEntityType, showCreateForm]);

  const handleEditField = useCallback((field: SchemaField) => editField(field.id), [editField]);

  const handleBulkArchive = useCallback(() => {
    if (selectedSchemaFields) {
      const hasSystemRequired = selectedSchemaFields.some((schemaField) => !schemaField.canArchive);
      const hasUsedInWorkflow = selectedSchemaFields.some(
        (schemaField) => schemaField.usedInWorkflow
      );
      if (hasSystemRequired || hasUsedInWorkflow) {
        setShowArchiveConfirm(true);
      } else {
        const updatedSchemaFields = selectedSchemaFields.map((schemaField) => ({
          ...schemaField,
          archived: true,
        }));
        onBulkUpdateField(updatedSchemaFields);
        setSelectedSchemaFields([]);
      }
    }
  }, [onBulkUpdateField, selectedSchemaFields]);

  const handleBulkUnarchive = useCallback(() => {
    if (selectedSchemaFields) {
      const updatedSchemaFields = selectedSchemaFields.map((schemaField) => ({
        ...schemaField,
        archived: false,
      }));
      onBulkUpdateField(updatedSchemaFields);
      setSelectedSchemaFields([]);
    }
  }, [onBulkUpdateField, selectedSchemaFields]);

  const handleConfirmArchive = useCallback(() => {
    if (selectedSchemaFields) {
      const allowedSchemaFields = selectedSchemaFields.filter(
        (schemaField) => !(!schemaField.canArchive || schemaField.usedInWorkflow)
      );
      const updatedSchemaFields = allowedSchemaFields.map((schemaField) => ({
        ...schemaField,
        archived: true,
      }));
      if (updatedSchemaFields.length) {
        onBulkUpdateField(updatedSchemaFields);
        setSelectedSchemaFields([]);
      }
    }
    setShowArchiveConfirm(false);
  }, [onBulkUpdateField, selectedSchemaFields]);

  const handleCloseAlert = useCallback(() => {
    onUpdateMetadata({ phiPromotionBannerDismissed: true });
  }, [onUpdateMetadata]);

  useEffect(() => {
    setSelectedSchemaFields([]);
  }, [entityType, isArchiveMode]);

  if (!entityType) {
    return null;
  }

  return (
    <AnalyticsContext.Provider value={analytics}>
      <SettingPage
        actions={
          isArchiveMode ? (
            <Button onClick={handleExitArchive}>
              {intl.formatMessage(messages.backToArchived)}
            </Button>
          ) : (
            <div className={styles.actions}>
              <Button onClick={handleEnterArchive}>
                {intl.formatMessage(messages.viewArchived)}
              </Button>
              <Button onClick={handleAddCustomField} type="primary">
                {intl.formatMessage(messages.addCustomField)}
              </Button>
            </div>
          )
        }
        pageContentClassName={styles.pageContent}
        title={intl.formatMessage(messages.title)}
      >
        <div className={styles.container}>
          {showPhiPromotionAlert && (
            <Alert
              closable
              message={intl.formatMessage(messages.phiAlert)}
              onClose={handleCloseAlert}
              showIcon
              type="info"
            />
          )}
          <div className={styles.topContainer}>
            <div className={styles.tabs}>
              <Tabs activeKey={entityType} onTabClick={handleTabClick}>
                <Tabs.TabPane
                  key={EntityType.COMPANY}
                  tab={intl.formatMessage(messages.companies)}
                />
                <Tabs.TabPane key={EntityType.PERSON} tab={intl.formatMessage(messages.people)} />
                <Tabs.TabPane key={EntityType.DEAL} tab={intl.formatMessage(messages.deals)} />
                <Tabs.TabPane
                  key={EntityType.ACTIVITY}
                  tab={intl.formatMessage(messages.acitivities)}
                />
              </Tabs>
            </div>
            <VisibilityFilter />
            <TypeFilter />
            <SearchBox
              analyticIssuer={analytics}
              placeholder={intl.formatMessage(messages.placeholder)}
            />
          </div>

          {selectedSchemaFields.length > 0 && (
            <div className={styles.checkBoxContent}>
              <span className={styles.checkBoxMessage}>
                {intl.formatMessage(messages.checkBoxMessage, {
                  count: selectedSchemaFields.length,
                })}
              </span>
              <div className={styles.buttonContainer}>
                {isArchiveMode ? (
                  <Button
                    leftIcon={<FontAwesomeIcon icon={faArchive} />}
                    onClick={handleBulkUnarchive}
                  >
                    {intl.formatMessage(messages.unarchive)}
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => setBulkEditPaneOpen(true)} type="link">
                      {intl.formatMessage(messages.editFieldAccessButton)}
                    </Button>
                    <Popconfirm
                      cancelText={intl.formatMessage(messages.cancelButton)}
                      description={intl.formatMessage(messages.popConfirmDescription)}
                      okText={intl.formatMessage(messages.okButton)}
                      onCancel={() => setShowArchiveConfirm(false)}
                      onConfirm={handleConfirmArchive}
                      open={showArchiveConfirm}
                      overlayClassName={styles.popConfirm}
                      placement="leftBottom"
                      title={intl.formatMessage(messages.popConfirmTitle)}
                    >
                      <Button
                        leftIcon={<FontAwesomeIcon icon={faArchive} />}
                        onClick={handleBulkArchive}
                      >
                        {intl.formatMessage(messages.archive)}
                      </Button>
                    </Popconfirm>
                  </>
                )}
              </div>
            </div>
          )}

          <FieldsTable
            onEditSchemaField={handleEditField}
            onSelectSchemaFields={setSelectedSchemaFields}
            selectedSchemaFieldsKeys={selectedSchemaFieldsKeys}
          />

          {createFormVisible && (
            <CreateEditPane
              analyticIssuer={analytics}
              onHide={hideCreateForm}
              onSave={handleCreateField}
            />
          )}

          {editFormVisible && <EditPane analyticIssuer={analytics} />}
          {bulkEditPaneOpen && (
            <BulkEditPane
              open={bulkEditPaneOpen}
              selectedSchemaFields={selectedSchemaFields}
              setOpen={setBulkEditPaneOpen}
            />
          )}
        </div>
      </SettingPage>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => {
  const phiPromotionBannerDismissed = getPhiPromotionAlertDismissed(state);
  const isOwner = isCurrentUserOwner(state);

  return {
    customFields: getCustomFieldsForEntity(state)(getActiveEntityType(state)),
    editFormVisible: isEditedFormVisible(state),
    entityType: getActiveEntityType(state),
    isArchiveMode: isArchiveMode(state),
    loading: isLoading(state),
    schema: getEntitySchemaFields(state)(getActiveEntityType(state)),
    showPhiPromotionAlert: !phiPromotionBannerDismissed && isOwner,
  };
};

const mapDispatchToProps = {
  cancelEditField,
  editField: editField.request,
  enterArchiveMode,
  exitArchiveMode,
  onBulkUpdateField: bulkUpdateField.request,
  onUpdateMetadata: updateMetadata.request,
  setActiveEntityType,
  setCreateEditCustomField,
  setCreateEditEntityType,
  updateSchema: updateSchema.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageFields);
